import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PageContainer from '../components/Layout/PageContainer';
import { uploadLecture } from '../api/sttApi';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import '../styles/lectureUpload.css';

const languageOptions = [
  { labelKey: 'language_en_general', value: 'en-general' },
  { labelKey: 'language_en_india', value: 'en-india' },
  { labelKey: 'language_zh_cn', value: 'zh-cn' },
  { labelKey: 'language_zh_tw', value: 'zh-tw' },
  { labelKey: 'language_yue_hk', value: 'yue-hk' },
];

const taskOptions = [
  { label: 'Transcript + Summary (recommended)', value: 'stt+summary' },
  { label: 'Transcript only', value: 'stt' },
];


function FloatingSttAd() {
  const [vw, setVw] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isMobile = vw <= 640;

  const containerStyle = {
    position: 'fixed',
    zIndex: 9000,
    right:16,
    boxSizing: 'border-box',
    pointerEvents: 'auto',
  };
  
  const labelStyle = {
    fontSize: 11,
    color: '#9ca3af',
    marginBottom: 4,
    textAlign: 'left',
  };

  const cardStyle = {
    width: isMobile ? 300 + 16 : 320,
    maxWidth: '92vw',
    padding: 8,
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    boxShadow: '0 2px 10px rgba(15,23,42,0.15)',
  };



  const frameCommon = {
    border: 0,
    display: 'block',
    margin: '0 auto',
  };

  return (
    <div className="stt-ad-box" style={containerStyle}>
      <div style={cardStyle}>
        <div style={labelStyle}>Sponsored</div>
        {isMobile ? (
          <iframe
            title="stt-ad-mobile-300x100"
            src="/hilltop-300x100.html"
            sandbox="allow-scripts allow-same-origin"
            referrerPolicy="no-referrer"
            loading="lazy"
            style={{ ...frameCommon, width: 300, height: 100 }}
          />
        ) : (
          <iframe
            title="stt-ad-desktop-300x250"
            src="/hilltop-300x250.html"
            sandbox="allow-scripts allow-same-origin"
            referrerPolicy="no-referrer"
            loading="lazy"
            style={{ ...frameCommon, width: 300, height: 250 }}
          />
        )}
      </div>
    </div>
  );
}

const LectureUploadPage = () => {
  const {
    user,
    loading,
    signInWithGoogle,
  } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const getLabel = (key, fallback) => {
    const value = t(key);
    if (!value || value === key) return fallback;
    return value;
  };

  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [tags, setTags] = useState('');
  const [languageMode, setLanguageMode] = useState('auto');
  const [languageCode, setLanguageCode] = useState(languageOptions[0].value);
  const [task, setTask] = useState(taskOptions[0].value);
  const [fileKey, setFileKey] = useState(0);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [copyMessage, setCopyMessage] = useState('');
  const successRef = useRef(null);
  const step2Ref = useRef(null);
  const location = useLocation();
  const [extFilename, setExtFilename] = useState(null);
  const [extMode, setExtMode] = useState(null);
  const [fromExtension, setFromExtension] = useState(false);

  const bannerDescription = extFilename
  ? getLabel(
      'sttExtensionBannerDescription',
      'Your recording {{filename}} has been downloaded to your device.'
    ).replace('{{filename}}', extFilename)
  : '';

  const uploadCta = useMemo(() => {
    if (uploading) return t('uploadUploading');
    return t('uploadButton');
  }, [uploading, t]);

  const authBadge = useMemo(() => {
    if (loading) {
      return { text: t('authChecking'), tone: 'status-processing' };
    }
    if (user) {
      return { text: t('authSignedIn'), tone: 'status-done' };
    }
    return { text: t('authGuest'), tone: 'status-processing' };
  }, [loading, user, t]);

  const resetState = () => {
    setFile(null);
    setFileKey((value) => value + 1);
    setTitle('');
    setSubject('');
    setTags('');
    setLanguageMode('auto');
    setLanguageCode(languageOptions[0].value);
    setTask(taskOptions[0].value);
  };

  useEffect(() => {
    if (result && successRef.current) {
      successRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [result]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fe = params.get('fromExtension');
    const fn = params.get('filename');
    const md = params.get('mode');

    if (fe === '1' && fn) {
      setFromExtension(true);
      setExtFilename(fn);
      setExtMode(md || null);
    }
  }, [location.search]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.source !== window) return;
      const { data } = event;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'STT_RECORDING_SAVED') {
        const { filename, mode } = data;

        setFromExtension(true);
        if (filename) setExtFilename(filename);
        if (mode) setExtMode(mode || null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (fromExtension && extFilename && step2Ref.current) {
      step2Ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [fromExtension, extFilename]);

  useEffect(() => {
    return () => {
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
    };
  }, [filePreviewUrl]);

  const handleOpenRecorderClick = () => {
    try {
      window.postMessage({ type: 'STT_PAGE_START_RECORDER' }, '*');
    } catch (e) {
      console.error('Failed to send recorder start message', e);
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);

    setFilePreviewUrl((prevUrl) => {
      if (prevUrl) {
        URL.revokeObjectURL(prevUrl);
      }
      if (!selectedFile) return null;
      return URL.createObjectURL(selectedFile);
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!user) {
      signInWithGoogle();
      return;
    }

    if (!file) {
      setError(t('errorFileRequired'));
      return;
    }

    const metadata = {
      title: title.trim() || undefined,
      subject: subject.trim() || undefined,
      tags: tags.trim() || undefined,
      task,
      languageMode,
      languageCode: languageMode === 'manual' ? languageCode : null,
    };

    const formData = new FormData();
    formData.append('file', file);
    formData.append('context_type', 'lecture');
    formData.append('task', task);

    setUploading(true);
    try {
      const response = await uploadLecture(formData, metadata);
      setResult(response);
      resetState();
      if (successRef.current) {
        successRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (apiError) {
      setResult(null);
      setError(apiError.message || t('errorUploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleCopyLectureId = async () => {
    if (!result?.context_id) return;
    try {
      await navigator.clipboard.writeText(result.context_id);
      setCopyMessage(t('copy_success'));
      setTimeout(() => setCopyMessage(''), 2000);
    } catch (err) {
      console.error('Failed to copy lecture ID', err);
    }
  };

  const handleStatusNavigation = () => {
    if (!result) return;
    const params = new URLSearchParams();
    if (result.request_id) params.append('request_id', result.request_id);
    if (result.context_id) params.append('context_id', result.context_id);
    navigate(`/lecture/status?${params.toString()}`);
  };

  return (
    <>
      <PageContainer>
        <div className="card upload-hero">
          <div className="card-heading">
            <h1 className="card-title">{t('uploadTitle')}</h1>
            <span className={`status-badge ${authBadge.tone}`}>{authBadge.text}</span>
          </div>
          <p>{t('uploadSubtitle')}</p>
          <p className="status-helper-text">
            {t('uploadFormatsHint')}
          </p>
          <div className="hero-links">
            <Link to="/lecture/status">{t('quickStatus')}</Link>
            <span className="hero-divider">|</span>
            <Link to="/lecture/pricing">{t('quickPricing')}</Link>
            <span className="hero-divider">|</span>
            <Link to="/lecture/faq">{t('quickFaq')}</Link>
          </div>
        </div>

        <div className="card upload-card" style={{ marginTop: '16px', marginBottom: '16px' }}>
          <section className="stt-step1-section">
            <h2 style={{ fontSize: '18px', marginBottom: '8px' }}>
              {getLabel('sttStep1Title', 'Step 1 — Record your lecture')}
            </h2>
            <p style={{ fontSize: '13px', color: '#555', marginBottom: '8px' }}>
              {getLabel(
                'sttStep1Description',
                'Use our free Chrome extension to record your lecture audio.'
              )}
            </p>
            <button
              type="button"
              onClick={handleOpenRecorderClick}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#1677ff',
                color: '#fff',
                fontSize: '14px',
                cursor: 'pointer',
                marginRight: '8px',
              }}
            >
              {getLabel('sttOpenRecorderButton', 'Open Recorder')}
            </button>
            <span style={{ fontSize: '12px', color: '#666' }}>
              {getLabel(
                'sttOpenRecorderHint',
                'Chrome extension installation is required before use.'
              )}
            </span>
          </section>
        </div>

        <div className="card upload-card">
          <div className="upload-grid">
            <div className="upload-form">
              <section className="stt-step2-section" ref={step2Ref}>
                <h2 style={{ fontSize: '18px', marginBottom: '12px' }}>
                  {getLabel('sttStep2Title', 'Step 2 — Upload your recording')}
                </h2>

                {fromExtension && extFilename && (
                  <div
                    className="extension-banner"
                    style={{
                      background: '#f0f9ff',
                      border: '1px solid #91d5ff',
                      padding: '12px',
                      borderRadius: '6px',
                      marginBottom: '12px',
                    }}
                  >
                    <h3 style={{ margin: 0, fontSize: '15px' }}>
                      {getLabel('sttExtensionBannerTitle', 'Recording ready to upload')}
                    </h3>
                    <p style={{ margin: '4px 0 4px', fontSize: '13px' }}>
                      {bannerDescription}
                    </p>
                    <p style={{ margin: 0, fontSize: '13px' }}>
                      {getLabel(
                        'sttExtensionBannerInstruction',
                        'Please click “Select File” below and choose this file to start transcription.'
                      )}
                    </p>
                    {extMode && (
                      <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#555' }}>
                        {getLabel('sttExtensionBannerModePrefix', 'Capture mode:')}{' '}
                        {extMode === 'tab'
                          ? getLabel('sttModeTab', 'Browser tab audio')
                          : getLabel('sttModeMic', 'Microphone recording')}
                      </p>
                    )}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="input-group">
                    <label htmlFor="lecture-file">{t('uploadSelectFile')}</label>
                    <input
                      key={fileKey}
                      id="lecture-file"
                      name="lecture-file"
                      type="file"
                      accept=".webm,.m4a,.mp3,.wav"
                      onChange={handleFileChange}
                    />
                    {file && (
                      <>
                        <span className="status-helper-text">{t('uploadSelectedFile')}: {file.name}</span>

                        {filePreviewUrl && (
                          <div style={{ marginTop: '8px' }}>
                            <audio
                              controls
                              src={filePreviewUrl}
                              style={{ width: '100%' }}
                              onTimeUpdate={(event) => {
                                const LIMIT = 15;  // preview length in seconds
                                if (event.target.currentTime > LIMIT) {
                                  event.target.pause();
                                  event.target.currentTime = 0;
                                }
                              }}
                            />

                            <p
                              style={{
                                marginTop: '4px',
                                fontSize: '12px',
                                color: '#555',
                            }}
                          >
                            {getLabel(
                              'sttPreviewHint',
                              'Preview is limited to the first 15 seconds. The full recording will be uploaded when you submit.'
                            )}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="metadata-grid">
                    <div className="input-group">
                      <label htmlFor="lecture-title">{t('uploadLectureTitle')}</label>
                      <input
                        id="lecture-title"
                        type="text"
                        placeholder={t('uploadLectureTitlePlaceholder')}
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                      />
                    </div>

                    <div className="input-group">
                      <label htmlFor="lecture-subject">{t('uploadSubject')}</label>
                      <input
                        id="lecture-subject"
                        type="text"
                        placeholder={t('uploadSubjectPlaceholder')}
                        value={subject}
                        onChange={(event) => setSubject(event.target.value)}
                      />
                    </div>

                    <div className="input-group">
                      <label htmlFor="lecture-tags">{t('uploadTags')}</label>
                      <input
                        id="lecture-tags"
                        type="text"
                        placeholder={t('uploadTagsPlaceholder')}
                        value={tags}
                        onChange={(event) => setTags(event.target.value)}
                      />
                    </div>

                  </div>

                  <div className="input-group">
                    <label>{t('uploadLocaleLabel')}</label>
                    <div className="radio-stack">
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="language-mode"
                          value="auto"
                          checked={languageMode === 'auto'}
                          onChange={() => {
                            setLanguageMode('auto');
                            setLanguageCode(languageOptions[0].value);
                          }}
                        />
                        {t('language_auto_detect')}
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="language-mode"
                          value="manual"
                          checked={languageMode === 'manual'}
                          onChange={() => setLanguageMode('manual')}
                        />
                        {t('language_manual')}
                      </label>
                    </div>
                    {languageMode === 'manual' && (
                      <select
                        id="lecture-language"
                        value={languageCode}
                        onChange={(event) => setLanguageCode(event.target.value)}
                      >
                        {languageOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(option.labelKey)}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="input-group">
                    <label>{t('uploadTaskLabel')}</label>
                    <div className="radio-stack">
                      {taskOptions.map((option) => (
                        <label key={option.value} className="radio-option">
                          <input
                            type="radio"
                            name="task"
                            value={option.value}
                            checked={task === option.value}
                            onChange={(event) => setTask(event.target.value)}
                          />
                          {t(option.value === 'stt+summary' ? 'uploadTaskTranscriptSummary' : 'uploadTaskTranscriptOnly')}
                        </label>
                      ))}
                    </div>
                  </div>

                  {error && <div className="error-text">{error}</div>}

                  <div className="upload-actions">
                    <button
                      type="submit"
                      className="primary-button"
                      disabled={uploading}
                    >
                      {uploadCta}
                    </button>
                    <div className="info-notice">
                      {t('uploadStatusHint')}
                    </div>
                  </div>
                </form>
              </section>
            </div>

            <div className="upload-aside">
              <div className="callout">
                <h3>{t('tipsTitle')}</h3>
                <ul>
                  <li>{t('tipsItem1')}</li>
                  <li>{t('tipsItem2')}</li>
                  <li>{t('tipsItem3')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {result && (
          <div className="success-card" ref={successRef}>
            <div className="success-heading">
              <span className="success-icon" aria-hidden="true"> {'\u2713'}</span>
              <div>
                <div className="success-title">{t('upload_success_headline')}</div>
                <div className="success-subtitle">{t('upload_success_subtitle')}</div>
              </div>
            </div>

            <div className="success-meta">
              <div className="meta-row">
                <span className="meta-label">{t('uploadRequestIdLabel')}</span>
                <code className="meta-value">{result.request_id}</code>
              </div>
              <div className="meta-row lecture-id-row">
                <span className="label">{t('upload_lecture_id_row_label')}</span>
                <span className="value"><code>{result.context_id}</code></span>
                <button
                  type="button"
                  className="copy-button"
                  onClick={handleCopyLectureId}
                >
                  {t('upload_copy_id')}
                </button>
              </div>
            </div>

            {copyMessage && (
              <div className="status-helper-text">{copyMessage}</div>
            )}

            <button type="button" className="primary-button" onClick={handleStatusNavigation}>
              {t('uploadViewStatus')}
            </button>
          </div>
        )}
      </PageContainer>

      {/* Floating small ad card (bottom-left) */}
      <FloatingSttAd />
    </>
  );
};

export default LectureUploadPage;
